import * as React from 'react';
import {AirbnbSelectDesktopContent} from './SelectDesktopContent';
import {AirbnbSelectMobileContent} from './SelectMobileContent';
import {AirbnbSelectTrigger} from './SelectTrigger';
import {getOptionIndex} from './select.utils';
import {type AirbnbSelectProps, type SelectValue} from './types';
import {useDesktopSelect} from './useDesktopSelect';
import {useMobileSelectWheel} from './useMobileSelectWheel';
import {useSelectIds} from './useSelectIds';
import {useOutsideClick, useScreenResize, useCombinedRef} from '../../hooks';
import {DEVICE} from '../../lib/device';
import {cn} from '../../lib/cn';
import type {ReactNode} from 'react';

const AirbnbSelect = React.forwardRef(function AirbnbSelect<
  T = unknown,
  V extends SelectValue = string,
  L extends string | number | ReactNode = string,
>(
  {
    options = [],
    value,
    onChange,
    onBlur,
    label,
    topLabel,
    placeholder,
    getValueLabel,
    renderTrigger,
    disabled,
    loading,
    optional,
    tooltip,
    helper,
    error,
    invalid,
    hideErrorMessage,
    className,
    menuClassName,
    dropdownClassName,
    doneLabel = 'Done',
    mobileTitle,
    name,
    noOptionsMessage,
    filterOption,
  }: AirbnbSelectProps<T, V, L>,
  ref: React.Ref<HTMLButtonElement>,
) {
  const {isMatch: isMobile} = useScreenResize(DEVICE.mobileXL);
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const filteredOptions = filterOption ? options.filter(filterOption) : options;
  const hasValue = Boolean(value);
  const helperText = placeholder ?? label;
  const shouldDescribeHelperText = !hasValue && helperText !== label;
  const isBlocked = Boolean(disabled) || Boolean(loading);
  const triggerError = error ?? invalid;
  const {
    triggerId,
    labelId,
    valueId,
    helperTextId,
    errorId,
    describedErrorId,
    listboxId,
    describedBy,
    getOptionId,
  } = useSelectIds({name, hasValue, error, hideErrorMessage});
  const triggerHelperTextId = shouldDescribeHelperText ? helperTextId : undefined;
  const triggerDescribedBy = shouldDescribeHelperText ? describedBy : describedErrorId;
  const {
    mobileListRef,
    mobileScrollTop,
    pendingValue,
    syncPendingValue,
    handleOptionClick,
    handleScroll,
    moveByStep,
    moveToBoundary,
  } = useMobileSelectWheel({
    isMobile,
    isOpen,
    options: filteredOptions,
    value,
    disabled: isBlocked,
  });
  const {
    highlightedIndex,
    listRef: desktopListRef,
    setHighlightedIndex,
    setOptionRef,
    triggerRef: desktopTriggerRef,
    focusTrigger,
    handleSelect: handleDesktopSelect,
    handleMenuKeyDown,
    handleTriggerKeyDown,
  } = useDesktopSelect({
    isMobile,
    isOpen,
    options: filteredOptions,
    value,
    disabled: isBlocked,
    onChange,
  });
  const combinedRef = useCombinedRef(ref, desktopTriggerRef);
  const activeMobileIndex = getOptionIndex(filteredOptions, pendingValue);
  const valueLabel = value ? (getValueLabel?.(value) ?? String(value.label)) : undefined;

  useOutsideClick({
    elementRef: containerRef,
    onOutsideClick: () => setIsOpen(false),
    isDisabled: !isOpen || isMobile,
  });

  React.useEffect(() => {
    if (isBlocked) {
      setIsOpen(false);
    }
  }, [isBlocked]);

  React.useEffect(
    function setCorrectOptionIfThereIsOnlyValue() {
      if (value?.value === undefined || value.value === null || value.label !== '') {
        return;
      }

      const validOption = filteredOptions.find(option => option.value === value.value);

      if (validOption) {
        onChange?.(validOption);
      }
    },
    [onChange, filteredOptions, value],
  );

  const handleMobileOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (isBlocked && nextOpen) return;

      setIsOpen(nextOpen);
      syncPendingValue(value ?? null);

      if (!nextOpen) {
        focusTrigger();
      }
    },
    [focusTrigger, isBlocked, syncPendingValue, value],
  );

  const handleMobileDone = React.useCallback(() => {
    if (isBlocked) return;

    const finalOption = pendingValue;

    if (finalOption && finalOption.value !== value?.value) {
      onChange?.(finalOption);
    }

    setIsOpen(false);
    focusTrigger();
  }, [focusTrigger, isBlocked, onChange, pendingValue, value]);

  const handleTriggerClick = React.useCallback(() => {
    if (isBlocked) return;

    setIsOpen(prev => {
      const nextOpen = !prev;

      if (isMobile) {
        syncPendingValue(value ?? null);
      }

      return nextOpen;
    });
  }, [isBlocked, isMobile, syncPendingValue, value]);

  const handleRootTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (isMobile) {
      if (isBlocked) return;

      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Enter' ||
        event.key === ' '
      ) {
        event.preventDefault();
        syncPendingValue(value ?? null);
        setIsOpen(true);
      }

      return;
    }

    handleTriggerKeyDown(event, () => setIsOpen(true));
  };

  const handleMobileWheelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveByStep(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveByStep(-1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      moveToBoundary('start');
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      moveToBoundary('end');
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleMobileDone();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      handleMobileOpenChange(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full max-w-[var(--max-field-width)]', className)}
    >
      {name && (
        <input type="hidden" name={name} value={value ? String(value.value) : ''} />
      )}
      {renderTrigger ? (
        renderTrigger({
          id: triggerId,
          open: isOpen,
          label,
          topLabel,
          helperText,
          value,
          valueLabel,
          disabled: isBlocked,
          loading,
          optional,
          tooltip,
          helper,
          error,
          invalid,
          listboxId,
          describedBy: triggerDescribedBy,
          triggerRef: combinedRef,
          onClick: handleTriggerClick,
          onKeyDown: handleRootTriggerKeyDown,
          onBlur,
        })
      ) : (
        <AirbnbSelectTrigger
          id={triggerId}
          ref={combinedRef}
          open={isOpen}
          label={label}
          topLabel={topLabel}
          helperText={helperText}
          valueLabel={valueLabel}
          disabled={disabled}
          loading={loading}
          optional={optional}
          tooltip={tooltip}
          helper={helper}
          error={triggerError}
          hideErrorMessage={hideErrorMessage}
          labelId={labelId}
          valueId={valueId}
          helperTextId={triggerHelperTextId}
          errorId={error ? errorId : undefined}
          listboxId={listboxId}
          describedBy={triggerDescribedBy}
          onClick={handleTriggerClick}
          onKeyDown={handleRootTriggerKeyDown}
          onBlur={onBlur}
        />
      )}
      {isMobile ? (
        <AirbnbSelectMobileContent
          open={isOpen}
          onOpenChange={handleMobileOpenChange}
          onClose={() => handleMobileOpenChange(false)}
          listboxId={listboxId}
          labelId={labelId}
          label={label}
          mobileTitle={mobileTitle}
          doneLabel={doneLabel}
          errorId={describedErrorId}
          options={filteredOptions}
          disabled={isBlocked}
          menuClassName={menuClassName}
          scrollTop={mobileScrollTop}
          activeIndex={activeMobileIndex}
          listRef={mobileListRef}
          onScroll={handleScroll}
          onOptionClick={handleOptionClick}
          onKeyDown={handleMobileWheelKeyDown}
          onDone={handleMobileDone}
          getOptionId={getOptionId}
          noOptionsMessage={noOptionsMessage}
        />
      ) : (
        <AirbnbSelectDesktopContent
          isOpen={isOpen}
          listboxId={listboxId}
          labelId={labelId}
          errorId={describedErrorId}
          options={filteredOptions}
          value={value}
          highlightedIndex={highlightedIndex}
          onOptionClick={option => {
            handleDesktopSelect(option);
            setIsOpen(false);
            focusTrigger();
          }}
          onOptionHover={setHighlightedIndex}
          onKeyDown={event => {
            handleMenuKeyDown(event, () => setIsOpen(false));
          }}
          disabled={isBlocked}
          menuClassName={menuClassName}
          dropdownClassName={dropdownClassName}
          listRef={desktopListRef}
          setOptionRef={setOptionRef}
          getOptionId={getOptionId}
          noOptionsMessage={noOptionsMessage}
        />
      )}
    </div>
  );
}) as <
  T = unknown,
  V extends SelectValue = string,
  L extends string | number | ReactNode = string,
>(
  props: AirbnbSelectProps<T, V, L> & {ref?: React.Ref<HTMLButtonElement>},
) => React.ReactElement;

export {AirbnbSelect};
export type {AirbnbSelectProps};
